







































import Vue from 'vue'
import { Component, Watch } from 'vue-property-decorator'
import { namespace, Getter, State } from 'vuex-class'
import { isNew } from 'vuexstore/helpers/post'
import SurfaceHeader from 'vuecomponents/surface_header.vue'
import SurfaceMapGoogle from 'vuecomponents/surface_map_google.vue'
import SurfaceMapPostPreviewPanel from 'vuecomponents/surface_map_post_preview_panel.vue'
import BaseIcon from 'vuecomponents/base_icon.vue'
import {
  GeoMap,
  LatLng,
  PostMarkerObject,
  Geocoder,
  GeocoderStatus,
  getBoundsZoomLevel,
  PostConnectionObject,
  NARNIA_LATLNG,
} from 'bits/map'
import DropToPostMixin from 'vuemixins/drop_to_post_mixin'
import { Post, Cid, PostAttributes } from 'types'
import {
  extractPostAttributesArrayFromClipboardData,
  extractPostAttributesFromClipboardData,
  PasteType,
} from 'bits/clipboard'
import { trackEvent } from 'bits/analytics'
import window from 'bits/global'
import { isEventOnAnInputField } from 'bits/dom'

const postModule = namespace('post')
const postConnectionModule = namespace('postConnection')
const mapModule = namespace('map')
const attachmentModule = namespace('attachment')
const browserModule = namespace('browser')
const composerModal = namespace('composerModal')
const draft = namespace('draft')

interface NewPost {
  attributes?: {
    file?
    attachment?
    content?
  }
}

@Component({
  mixins: [DropToPostMixin],
  components: {
    BaseIcon,
    SurfaceHeader,
    SurfaceMapPostPreviewPanel,
    SurfaceMapGoogle,
  },
})
class SurfaceContainerMap extends Vue {
  zoom = 2
  latitude = 0
  longitude = 0
  previewBeingDragged = false
  @State((state) => state.constants.currentCountry) currentCountry!: string
  @Getter xHeader!: boolean
  @Getter themeId!: number
  @Getter backgroundColor!: string
  @Getter isPostable!: boolean
  @Getter xTallAttachmentPickerBox!: boolean
  @Getter isUsingNewComposerModal!: boolean
  @browserModule.Getter windowWidth
  @postModule.Getter postsArray!: Post[]
  @postModule.Getter postBeingEdited!: Post | null
  @postModule.Getter postBeingEditedCid!: Cid | null
  @postModule.Getter getPostByServerId!: (Id) => Post
  @postModule.Getter getPostByCid!: (cid) => Post | null
  @postModule.Getter sortedPosts!: Post[]
  @postModule.Getter isInitialPaginatedDataLoadDone!: boolean
  @postModule.Getter mostRecentlyChangedGeolocationPostCid!: Cid | null
  @mapModule.Getter map
  @mapModule.Getter isPreviewPanelShown
  @mapModule.Action enableMapFittingMarkersBounds
  @mapModule.Action disableMapFittingMarkersBounds
  @mapModule.Action setMapBeingUsed
  @mapModule.Action toggleMapPreviewPanel
  @attachmentModule.Action uploadFileToPickerBox
  @attachmentModule.Action cancelUploadFileToPickerBox
  @postModule.Action saveContent
  @postModule.Action createPost
  @postModule.Action resetMostRecentlyChangedGeolocationPostCid
  @postConnectionModule.Getter connections
  @draft.Action startNewDraft!: (payload: { attributes: PostAttributes; shouldStartEditing: boolean }) => void
  @composerModal.Action('dock') dockComposerModal!: () => void

  enableDropToPost!: (wallContainer: Element, callback?: (NewPost) => void) => void // from DropToPostMixin
  disableDropToPost!: (wallContainer: Element) => void // from DropToPostMixin

  get currentPosts(): Post[] {
    return this.postsArray.filter((p) => !isNew(p))
  }

  get postZIndices(): Record<Cid, number> {
    const zIndices = {}
    this.sortedPosts.forEach((post, index, array) => {
      zIndices[post.cid] = 200 + array.length - index
    })
    return zIndices
  }

  get allowPreviewPanel(): boolean {
    return this.sortedPosts.length > 0
  }

  get markers(): PostMarkerObject[] {
    if (!this.isInitialPaginatedDataLoadDone) {
      return []
    }
    const locationlessPosts: number[] = []
    const markers = this.currentPosts.map((p: Post, index: number, array: Post[]) => {
      if (!p.location_point || !p.location_point.longitude || !p.location_point.latitude) {
        locationlessPosts.push(index)
        return { id: p.cid, position: NARNIA_LATLNG, post: p, zIndex: index }
      }
      return {
        id: p.cid,
        position: {
          lng: p.location_point.longitude,
          lat: p.location_point.latitude,
        },
        post: p,
        zIndex: this.postZIndices[p.cid],
      }
    })
    /**
     * * Antarctica:
     * * Latitude from -75 -> -90. 82.5 is the center of the span. At 15 markers, we add a different row:
     *   -82.5 is the center latitude of Antartica
     *   `- locationlessPosts.length / 15` will start the rows a bit lower to have space for new row
     *   `+ 3 * (x % (1 + locationlessPosts.length / 15))` will move the marker a bit above to distribute
     * the marker upwards from the starting location. Modular will repeat the jigsaw graph for every
     * (1 + locationlessPosts.length / 15) markers, see https://www.wolframalpha.com/input/?i=y+%3D+%28x+mod+5%29
     * We want to add a new row for every 15 markers, therefore we increase the divisor every 15 marker
     * (hence locationlessPosts.length / 15))
     *
     * * Longitude from -180 -> 180:
     *   Distribute post longitudes equidistantly starting from -180 to 180
     *   With the marker index i in the total of len markers:
     *    - Total number of gaps between posts will be (len + 1): len - 1 gaps in between and 2 gaps on the 2 sides.
     *    - The marker of post i will have the offset of (i+1) gaps to the left of the marker.
     *  At len = 1, the single post should be at lng 0 = 1/2 of 360 (starting from -180)
     *  At len = 2, the two post should be at the 1/3 and 2/3 of 360 (starting from -180)
     *    -> lng = [-60, 120]. Equidistant from the International Date Line and from each other.
     *  At len = 3 post should be at the 1/4, 2/4 and 3/4 of 360 (starting from -180)
     *    -> lng = [-90, 0, 90]. Equidistant from the International Date Line and from each other.
     *  At i = x, len = y, post should be at (x+1)/(y+1) of 360 (starting from -180)
     *   Hence the equation lng = -180 + 360(i+1)/(len+1)
     */
    const NUMBER_OF_LOCATIONLESS_POSTS = locationlessPosts.length
    const LOWEST_LATITUDE = -82.5 - NUMBER_OF_LOCATIONLESS_POSTS / 15
    locationlessPosts.forEach((x) => {
      markers[x].position = {
        // 82.5
        lat: LOWEST_LATITUDE + 3 * (x % (1 + NUMBER_OF_LOCATIONLESS_POSTS / 15)),
        lng: -180 + ((x + 1) * 360) / (NUMBER_OF_LOCATIONLESS_POSTS + 1),
      }
    })
    return markers
  }

  get mapBrowserMobileStyle() {
    if (this.windowWidth < 1174) {
      return { 'background-image': `linear-gradient(transparent,${this.backgroundColor} 10vh )` }
    }
    return { 'background-color': `${this.backgroundColor}` }
  }

  onMapInitialized(map: GeoMap) {
    this.setMapBeingUsed({ map })
  }

  onPreviewItemBeingReordered() {
    this.previewBeingDragged = true
  }
  onPreviewItemStopBeingReordered() {
    this.previewBeingDragged = false
  }

  decideDropToPostBehavior() {
    // If preview item is dragged, we disable drop to post
    if (this.previewBeingDragged) {
      this.disableDropToPost(this.$el)
    }
    // If there is a post being edited, drop will upload file to that post
    else if (this.postBeingEditedCid && this.xTallAttachmentPickerBox) {
      this.disableDropToPost(this.$el)
      this.enableDropToPost(this.$el, (post: NewPost) => {
        if (!post || !post.attributes) {
          return
        }
        if (post.attributes.file) {
          this.uploadFileToPickerBox({ file: post.attributes.file })
        }
        if (post.attributes.attachment) {
          this.saveContent({
            content: {
              link: post.attributes.attachment,
            },
          })
        }
      })
    }
    // If not, drops will create post
    else if (this.xTallAttachmentPickerBox) {
      this.disableDropToPost(this.$el)
      this.enableDropToPost(this.$el, (post: NewPost) => {
        this.enableMapFittingMarkersBounds()
        this.createPost(post)
      })
    } else {
      this.cancelUploadFileToPickerBox()
    }
  }

  @Watch('previewBeingDragged')
  respondToPreviewReordering() {
    this.decideDropToPostBehavior()
  }

  @Watch('postBeingEditedCid')
  respondToPostBeingEditedCid() {
    this.decideDropToPostBehavior()
  }

  @Watch('mostRecentlyChangedGeolocationPostCid')
  responseToPostCidChangedGeolocation(cid: Cid | null): void {
    this.$nextTick(() => {
      const post = this.getPostByCid(cid)
      if (
        !!this.map &&
        post?.location_point?.longitude !== null &&
        post?.location_point?.longitude !== undefined &&
        post?.location_point?.latitude !== null &&
        post?.location_point?.latitude !== undefined
      ) {
        this.map.panTo({ lat: post.location_point.latitude, lng: post.location_point.longitude })
      }
    })
    if (cid) {
      this.resetMostRecentlyChangedGeolocationPostCid()
    }
  }

  // serialize connections array with post data, resulting in a PostConnectionObject array
  get mapConnections(): PostConnectionObject[] {
    return this.connections
      .map((c): { id: any; from: Post; to: Post } | null => {
        const fromPost = this.getPostByServerId(c.from_wish_id)
        const toPost = this.getPostByServerId(c.to_wish_id)
        if (!fromPost) return null
        if (!toPost) return null
        return {
          ...c,
          from: fromPost,
          to: toPost,
        }
      })
      .filter((x) => !!x)
  }

  mounted() {
    this.enableMapFittingMarkersBounds()
    if (this.isPostable) {
      window.addEventListener('paste', this.handleOnPaste as (e: Event) => void)
      this.enableDropToPost(this.$el)
    } else {
      this.disableNewPostHandlers()
    }
    if (this.postsArray.length === 0 && this.currentCountry) {
      // Use geocoding service to get the lat, lng and zoomLevel for the country
      // (based on user IP) for setting the starting position
      // Only do this if there is no post (to reduce number of geocoding requests)
      const geocoder = new Geocoder()
      geocoder.geocode(
        {
          address: this.currentCountry,
        } /* can also use `city_name`, only return country_name of geodata from the backend for now */,
        (results, status) => {
          if (status === GeocoderStatus.OK) {
            this.latitude = results[0].geometry.location.lat()
            this.longitude = results[0].geometry.location.lng()
            const mapDiv = this.map.getDiv()
            const mapDim = {
              height: mapDiv.clientHeight,
              width: mapDiv.clientWidth,
            }
            this.zoom = getBoundsZoomLevel(results[0].geometry.viewport, mapDim)
          }
        },
      )
    }
  }

  disableNewPostHandlers() {
    window.removeEventListener('paste', this.handleOnPaste as (e: Event) => void)
    this.disableDropToPost(this.$el)
  }

  async onMapClicked(e: { latLng: LatLng; placeId?: string }) {
    // Do nothing
  }

  handleOnPaste(event: ClipboardEvent): void {
    event.stopPropagation()
    if (!this.isPostable) return

    if (isEventOnAnInputField(event)) {
      return
    }

    const data = extractPostAttributesArrayFromClipboardData(event.clipboardData)
    if (!data || !data.length) return

    // Pasting texts from some applications like Microsoft Word may also paste an image version of the text
    // we want to treat them as text
    const isRichTextPastedAsFile =
      event.clipboardData?.types?.includes('text/rtf') && event.clipboardData?.types?.includes('Files')

    // Create a post with attachment without filename
    // when PasteType is File
    const attachment = extractPostAttributesFromClipboardData(data, PasteType.File)
    if (attachment && !isRichTextPastedAsFile) {
      if (this.isUsingNewComposerModal) {
        this.startNewDraft({ attributes: attachment.postAttributes, shouldStartEditing: true })
        this.dockComposerModal()
      } else {
        this.createPost({ attributes: attachment.postAttributes })
      }
      trackEvent('Posts', 'add', `Paste ${attachment.pasteType}`)
      return
    }
    // Create a new post when PasteType is Link or Text or PostContentHash
    const dataWithoutFile = data.filter((datum) => {
      return datum?.pasteType !== PasteType.File
    })
    if (this.isUsingNewComposerModal) {
      this.startNewDraft({ attributes: dataWithoutFile[0].postAttributes, shouldStartEditing: true })
      this.dockComposerModal()
    } else {
      this.createPost({ attributes: dataWithoutFile[0].postAttributes })
    }
    trackEvent('Posts', 'add', `Paste ${dataWithoutFile[0].pasteType}`)
  }
}
export default SurfaceContainerMap
